import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Raffle.module.css';
import {
    CreateTicketBonusInput,
    PeriodType,
    raffleService,
    generateTicketCode
} from '../../services/raffle.service';
import { authService } from '../../services/auth.service';
import {
    getAllEnvironmentsService,
    Environment
} from '../../services/getAllEnvironment.service';

interface RaffleFormData {
    name: string;
    description: string;
    prizeType: string;
    period: PeriodType;
    startDate: string;
    endDate: string;
    isActive: boolean;
    autoIssue: boolean;
    isStackable: boolean;
    winAmount: string;
    ticketPrice: string;
    cashinFreeTicketsEnabled: boolean;
    cashinFreeTicketsPercent: string;
    environmentId: number | '';
}

const initialFormState: RaffleFormData = {
    name: '',
    description: '',
    prizeType: '',
    period: PeriodType.DAILY,
    startDate: '',
    endDate: '',
    isActive: true,
    autoIssue: false,
    isStackable: false,
    winAmount: '',
    ticketPrice: '1',
    cashinFreeTicketsEnabled: false,
    cashinFreeTicketsPercent: '1',
    environmentId: '',
};

const RaffleCreate = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<RaffleFormData>(initialFormState);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [environments, setEnvironments] = useState<Environment[]>([]);

    useEffect(() => {
        const currentUser = authService.getStoredUser();
        const isAdmin = currentUser?.role?.name === 'ADMIN';
        if (isAdmin) {
            getAllEnvironmentsService.getAllEnvironments()
                .then(setEnvironments)
                .catch(console.error);
        }
    }, []);


    const calculateEndDate = (startDate: string, period: PeriodType): string => {
        if (!startDate) return '';
        
        const start = new Date(startDate);
        const end = new Date(start);
        
        switch (period) {
            case PeriodType.DAILY:
                end.setDate(end.getDate() + 1);
                break;
            case PeriodType.WEEKLY:
                end.setDate(end.getDate() + 7);
                break;
            case PeriodType.MONTHLY:
                end.setMonth(end.getMonth() + 1);
                break;
        }
        
        // Convert to datetime-local format
        const localDateTime = new Date(end.getTime() - end.getTimezoneOffset() * 60000)
            .toISOString()
            .slice(0, 16);
        
        return localDateTime;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        
        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData((prev) => ({
                ...prev,
                [name]: checked,
            }));
        } else {
            setFormData((prev) => {
                const newData = {
                    ...prev,
                    [name]: value,
                };
                
                // Auto-generate end date when start date or period changes
                if (name === 'startDate' && value) {
                    newData.endDate = calculateEndDate(value, prev.period);
                } else if (name === 'period' && prev.startDate) {
                    newData.endDate = calculateEndDate(prev.startDate, value as PeriodType);
                }
                
                return newData;
            });
        }
    };

    const handleCancel = () => {
        navigate('/raffle');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!formData.prizeType.trim()) {
            setError('Please enter a prize type');
            return;
        }

        if (!formData.startDate) {
            setError('Please select a start date');
            return;
        }

        if (!formData.endDate) {
            setError('Please select an end date');
            return;
        }

        const ticketPrice = parseFloat(formData.ticketPrice);
        if (isNaN(ticketPrice) || ticketPrice <= 0) {
            setError('Please enter a valid ticket price');
            return;
        }

        let winAmount: number | null = null;
        if (formData.winAmount && formData.winAmount.trim() !== '') {
            winAmount = parseFloat(formData.winAmount);
            if (isNaN(winAmount) || winAmount <= 0) {
                setError('Please enter a valid win amount or leave it empty');
                return;
            }
        }

        const cashinPercent = parseFloat(formData.cashinFreeTicketsPercent);
        if (formData.cashinFreeTicketsEnabled && (isNaN(cashinPercent) || cashinPercent <= 0)) {
            setError('Please enter a valid cashin free tickets percent');
            return;
        }

        setSubmitting(true);

        try {
            const code = generateTicketCode(formData.prizeType.toUpperCase(), formData.period);
            
            const input: CreateTicketBonusInput = {
                name: formData.name,
                description: formData.description,
                code: code,
                period: formData.period,
                startDate: new Date(formData.startDate).toISOString(),
                endDate: new Date(formData.endDate).toISOString(),
                isActive: formData.isActive,
                autoIssue: formData.autoIssue,
                isStackable: formData.isStackable,
                conditions: {
                    prizeType: formData.prizeType.toUpperCase(),
                    winAmount: winAmount,
                    ticketPrice: ticketPrice,
                    cashinFreeTicketsEnabled: formData.cashinFreeTicketsEnabled,
                    cashinFreeTicketsPercent: formData.cashinFreeTicketsEnabled ? cashinPercent : undefined,
                },
                ...(formData.environmentId ? { environmentId: formData.environmentId } : {}),
            };

            await raffleService.createTicketBonus(input);
            setSuccess('Successfully created raffle ticket bonus!');
            setTimeout(() => {
                navigate('/raffle');
            }, 1500);
        } catch (err: any) {
            const errorMessage = err?.graphQLErrors?.[0]?.message || err?.message || 'Failed to create raffle ticket bonus';
            setError(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    const generatedCode = formData.prizeType ? generateTicketCode(formData.prizeType.toUpperCase(), formData.period) : '';

    return (
        <main className={styles.main}>
            <div className={styles.headerRow}>
                <h1 className={styles.title}>Create Raffle Ticket Bonus</h1>
            </div>

            <div className={styles.createFormContainer}>
                <form className={styles.form} onSubmit={handleSubmit}>
                    {error && <div className={styles.error}>{error}</div>}
                    {success && <div className={styles.success}>{success}</div>}

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Name *</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            placeholder="e.g., Car Raffle Daily Ticket"
                            required
                        />
                    </div>

                    {environments.length > 0 && (
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Environment *</label>
                            <select
                                name="environmentId"
                                value={formData.environmentId}
                                onChange={(e) => setFormData(prev => ({ ...prev, environmentId: e.target.value ? Number(e.target.value) : '' }))}
                                className={styles.formSelect}
                                required
                            >
                                <option value="">Select Environment</option>
                                {environments.map((env) => (
                                    <option key={env.id} value={env.id}>
                                        {env.name} - {env.host}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Description *</label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            className={styles.formTextarea}
                            placeholder="e.g., Daily raffle ticket for car prize."
                            rows={3}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Prize Type *</label>
                        <input
                            type="text"
                            name="prizeType"
                            value={formData.prizeType}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            placeholder="e.g., CAR, BIKE, PHONE"
                            required
                        />
                        <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Will be converted to uppercase
                        </small>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Period *</label>
                        <select
                            name="period"
                            value={formData.period}
                            onChange={handleInputChange}
                            className={styles.formSelect}
                            required
                        >
                            <option value={PeriodType.DAILY}>Daily</option>
                            <option value={PeriodType.WEEKLY}>Weekly</option>
                            <option value={PeriodType.MONTHLY}>Monthly</option>
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Ticket Price *</label>
                        <input
                            type="number"
                            name="ticketPrice"
                            value={formData.ticketPrice}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            placeholder="e.g., 1"
                            step="0.01"
                            min="0"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Win Amount</label>
                        <input
                            type="number"
                            name="winAmount"
                            value={formData.winAmount}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            placeholder="Optional - leave empty if not applicable"
                            step="0.01"
                            min="0"
                        />
                        <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            Optional - can be null
                        </small>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Start Date *</label>
                        <input
                            type="datetime-local"
                            name="startDate"
                            value={formData.startDate}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>End Date *</label>
                        <input
                            type="datetime-local"
                            name="endDate"
                            value={formData.endDate}
                            onChange={handleInputChange}
                            className={styles.formInput}
                            required
                        />
                    </div>

                    <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                            />
                            <span>Active</span>
                        </label>
                    </div>

                    {/* <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="autoIssue"
                                checked={formData.autoIssue}
                                onChange={handleInputChange}
                            />
                            <span>Auto Issue</span>
                        </label>
                    </div> */}

                    {/* <div className={styles.checkboxGroup}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                name="isStackable"
                                checked={formData.isStackable}
                                onChange={handleInputChange}
                            />
                            <span>Stackable</span>
                        </label>
                    </div> */}

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            className={styles.cancelBtn}
                            onClick={handleCancel}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.submitBtn}
                            disabled={submitting}
                        >
                            {submitting ? 'Creating...' : 'Create Raffle Ticket'}
                        </button>
                    </div>
                </form>
            </div>
        </main>
    );
};

export default RaffleCreate;
